import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close as CloseIcon, InfoOutlined as InfoOutlinedIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getUploadStatus } from '../../../store/api/chargeMaster';

const POLL_INTERVAL_MS = 3000;

export function getOverallStatusColor(status: any): string {
    if (!status?.steps?.some((s: any) => s.status !== 'PENDING')) return '#9e9e9e';
    if (status.isInProgress)                                        return '#ffc107'; // yellow
    if (status.steps.some((s: any) => s.status === 'FAILED'))      return '#f44336'; // red
    if (status.steps.every((s: any) => s.status === 'COMPLETED'))  return '#4caf50'; // green
    return '#9e9e9e';
}

function isTerminal(status: any) {
    if (!status) return false;
    return !status.isInProgress &&
        status.steps?.some((s: any) => s.status === 'COMPLETED' || s.status === 'FAILED');
}

function StatusChip({ status }: { status: string }) {
    const colorMap: Record<string, any> = {
        COMPLETED:   { label: 'COMPLETED',   color: '#4caf50' },
        FAILED:      { label: 'FAILED',      color: '#f44336' },
        IN_PROGRESS: { label: 'IN PROGRESS', color: '#1976d2' },
        PENDING:     { label: 'PENDING',     color: '#9e9e9e' },
    };
    const { label, color } = colorMap[status] ?? { label: status, color: '#9e9e9e' };
    return (
        <Chip
            label={label}
            size="small"
            sx={{
                backgroundColor: color,
                color: '#fff',
                fontWeight: 700,
                fontSize: '11px',
                borderRadius: '4px',
                height: '22px',
                letterSpacing: '0.04em',
            }}
        />
    );
}

function FailureReasonPopover({ reason }: { reason: string }) {
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    return (
        <>
            <Tooltip title="View failure details">
                <IconButton
                    size="small"
                    onClick={(e) => setAnchor(e.currentTarget)}
                    sx={{ p: 0.25, color: '#f44336' }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: '15px' }} />
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { maxWidth: 360, p: 1.5 } }}
            >
                <Typography variant="caption" sx={{ fontSize: '12px', color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Failure Reason
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {reason}
                </Typography>
            </Popover>
        </>
    );
}

function StepList({ steps }: { steps: any[] }) {
    return (
        <Box>
            {steps.map((step, i) => (
                <Box
                    key={i}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        borderBottom: i < steps.length - 1 ? '1px solid #f0f0f0' : 'none',
                        py: 0.75,
                        px: 1,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        {step.status === 'IN_PROGRESS' && (
                            <CircularProgress size={12} thickness={5} />
                        )}
                        <Typography variant="body2" sx={{ fontSize: '13px' }}>
                            {step.name}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <StatusChip status={step.status} />
                        {step.status === 'FAILED' && step.failureReason && (
                            <FailureReasonPopover reason={step.failureReason} />
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}


interface InlinePanelProps {
    status: any;
    onDismiss: () => void;
    onRefresh: () => void;
}

export function InlineUploadPanel({ status, onDismiss, onRefresh }: InlinePanelProps) {
    if (!status) return null;
    return (
        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', mb: 1 }}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}
            >
                <Box>
                    <Typography variant="subtitle2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {status.isInProgress ? 'Upload In Progress…' : 'Last Upload Status'}
                    </Typography>
                    {status.uploadedBy && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                            {status.uploadedBy}
                            {status.uploadedAt ? ` · ${new Date(status.uploadedAt).toLocaleString()}` : ''}
                        </Typography>
                    )}
                </Box>
                <Box display="flex" alignItems="center">
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={onRefresh}>
                            <RefreshIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Dismiss">
                        <IconButton size="small" onClick={onDismiss}>
                            <CloseIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <StepList steps={status.steps ?? []} />
        </Paper>
    );
}



interface StatusModalProps {
    open: boolean;
    onClose: () => void;
    status: any;
    onRefresh: () => void;
}

export function UploadStatusModal({ open, onClose, status, onRefresh }: StatusModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', py: 1.5 }}>
                <Grid container alignItems="center">
                    <Grid item xs={10}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 400 }}>
                            Charge Master Upload Status
                        </Typography>
                        {status?.uploadedBy && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                {status.uploadedBy}
                                {status.uploadedAt ? ` · ${new Date(status.uploadedAt).toLocaleString()}` : ''}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: 'right' }}>
                        <Tooltip title="Refresh">
                            <IconButton size="small" onClick={onRefresh} sx={{ mr: 0.5 }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {status ? (
                    <StepList steps={status.steps ?? []} />
                ) : (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

interface UseUploadStatusPollingProps {
    startPolling: boolean;
    onStatusUpdate: (status: any) => void;
    onPollingDone?: () => void;
}

export function useUploadStatusPolling({ startPolling, onStatusUpdate, onPollingDone }: UseUploadStatusPollingProps) {
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        try {
            const data = await getUploadStatus();
            onStatusUpdate(data);
            return data;
        } catch {
            return null;
        }
    }, [onStatusUpdate]);

    const startPollingLoop = useCallback(() => {
        if (pollingRef.current) return;
        pollingRef.current = setInterval(async () => {
            const data = await fetchStatus();
            if (data && isTerminal(data)) {
                stopPolling();
                if (onPollingDone) onPollingDone();
            }
        }, POLL_INTERVAL_MS);
    }, [fetchStatus, stopPolling, onPollingDone]);

    useEffect(() => {
        (async () => {
            const data = await fetchStatus();
            if (data?.isInProgress) startPollingLoop();
        })();
        return () => stopPolling();
    }, []);

    useEffect(() => {
        if (!startPolling) return;
        fetchStatus();
        startPollingLoop();
        return () => stopPolling();
    }, [startPolling]);

    return { fetchStatus };
}
